/**
 * @fileoverview Modal component for creating and editing Farmeando Aura themes.
 * Supports tabbed navigation, Drag & Drop uploads, batch deck media processing,
 * contrast text colors, and custom combo phrases.
 * 
 * @module components/Admin/Games/FarmeandoAura/FarmeandoAuraThemeModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { UploadMedia, UploadBatchMedia, SaveTheme } from './../../../../utilities/BL/FarmeandoAura';

/**
 * Default combo configuration levels for Farmeando Aura.
 */
const DEFAULT_COMBO_LEVELS = [
    {
        level: 1,
        threshold: 10,
        text: '🌟🌟 +10 DE AURA 🌟🌟',
        extraPoints: 15,
        fontSize: '4rem',
        specialEffect: 'confetti-light'
    },
    {
        level: 2,
        threshold: 20,
        text: '😎😎 +1,000 DE AURA 😎😎',
        extraPoints: 20,
        fontSize: '5rem',
        specialEffect: 'confetti-light'
    },
    {
        level: 3,
        threshold: 30,
        text: '🚀🚀 +10,000 DE AURA 🚀🚀',
        extraPoints: 25,
        fontSize: '6rem',
        specialEffect: 'confetti-light'
    },
    {
        level: 4,
        threshold: 40,
        text: '🔥🔥 +100,000 DE AURA 🔥🔥',
        extraPoints: 30,
        fontSize: '7rem',
        specialEffect: 'confetti-medium'
    },
    {
        level: 5,
        threshold: 50,
        text: '⚡⚡ +1,000,000 DE AURA ⚡⚡',
        extraPoints: 40,
        fontSize: '8rem',
        specialEffect: 'confetti-heavy'
    },
    {
        level: 6,
        threshold: 100,
        text: '🌌 MODO DIOS 🌌',
        extraPoints: 50,
        fontSize: '9rem',
        specialEffect: 'confetti-massive'
    }
];

/**
 * Empty default transformation deck array.
 */
const DEFAULT_TRANSFORMATION_DECK_PRESET = [];

/**
 * Reusable Drag & Drop Field component for individual media assets.
 * 
 * @function DragAndDropField
 * @param {Object} props - Component properties.
 * @param {string} props.fieldLabel - Text label displayed above the drop area.
 * @param {string} props.targetFieldName - Form property key being updated.
 * @param {string} props.mediaCategory - Category identifier for SkiaSharp optimization.
 * @param {string} props.currentValue - Current relative or absolute URL of the asset.
 * @param {Function} props.onUploadFile - Handler triggered when a file is dropped or selected.
 * @param {Function} props.resolveMediaUrl - Helper that resolves absolute URLs for preview.
 * @param {boolean} [props.isProcessing=false] - Whether an upload is in progress.
 * @param {string} [props.helpText] - Optional helper text below the dropzone.
 */
const DragAndDropField = ({
    fieldLabel,
    targetFieldName,
    mediaCategory,
    currentValue,
    onUploadFile,
    resolveMediaUrl,
    isProcessing = false,
    helpText = ''
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = () => {
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsDragHoverActive(false);
        const selectedFile = dropEvent.dataTransfer.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFile = changeEvent.target.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    return (
        <div className="mb-3">
            {fieldLabel && (
                <label className="form-label fw-bold text-dark mb-1">
                    {fieldLabel}:
                </label>
            )}
            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${
                    isDragHoverActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetFieldName}`).click()}
                style={{ minHeight: '90px', cursor: 'pointer' }}
            >
                {isProcessing ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y optimizando...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-4 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </>
                )}
                <input
                    id={`file-input-${targetFieldName}`}
                    type="file"
                    className="d-none"
                    accept="image/*,video/mp4"
                    onChange={handleNativeFileChange}
                />
            </div>
            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative">
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt="Vista previa"
                        style={{ maxHeight: '75px', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(errorEvent) => {
                            errorEvent.target.style.display = 'none';
                        }}
                    />
                    <div className="small text-truncate text-muted mt-1" style={{ maxWidth: '100%' }}>
                        {currentValue}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Component for uploading multiple deck images simultaneously with Drag & Drop.
 * 
 * @function BatchDeckDropzone
 * @param {Object} props - Component properties.
 * @param {Function} props.onUploadBatch - Handler for uploading an array of files.
 * @param {boolean} props.isUploading - Indicator if batch upload is currently active.
 */
const BatchDeckDropzone = ({ onUploadBatch, isUploading }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsHovered(true);
    };

    const handleDragLeave = () => {
        setIsHovered(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsHovered(false);
        const droppedFiles = Array.from(dropEvent.dataTransfer.files).filter((fileItem) =>
            fileItem.type.startsWith('image/')
        );
        if (droppedFiles.length > 0) {
            onUploadBatch(droppedFiles);
        }
    };

    const handleInputChange = (inputEvent) => {
        const selectedFiles = Array.from(inputEvent.target.files).filter((fileItem) =>
            fileItem.type.startsWith('image/')
        );
        if (selectedFiles.length > 0) {
            onUploadBatch(selectedFiles);
        }
    };

    return (
        <div
            className={`border border-2 border-dashed rounded-4 p-4 text-center mb-4 transition ${
                isHovered ? 'border-success bg-success-subtle shadow' : 'border-primary bg-light'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('batch-deck-file-input').click()}
            style={{ cursor: 'pointer' }}
        >
            <i className="bi bi-collection-play-fill text-primary fs-1 d-block mb-2"></i>
            <h5 className="fw-bold text-dark mb-1">Carga Masiva de Baraja de Transformaciones (1..50 imágenes)</h5>
            <p className="text-muted small mb-2">
                Selecciona o arrastra una colección completa de imágenes (sprites PNG/JPG) para poblar automáticamente los slots de la baraja.
            </p>
            {isUploading ? (
                <div className="alert alert-info py-2 d-inline-block mb-0">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <strong>Optimizando lote con SkiaSharp a 600x600 px...</strong>
                </div>
            ) : (
                <span className="btn btn-sm btn-outline-primary fw-bold px-3">
                    <i className="bi bi-plus-circle me-1"></i> Explorar Archivos Múltiples
                </span>
            )}
            <input
                id="batch-deck-file-input"
                type="file"
                multiple
                accept="image/*"
                className="d-none"
                onChange={handleInputChange}
            />
        </div>
    );
};

/**
 * Main Farmeando Aura Theme Modal component.
 * 
 * @function FarmeandoAuraThemeModal
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {Function} props.onCloseModal - Handler invoked to dismiss modal.
 * @param {Object} [props.initialThemeData=null] - Theme data when editing, or null for creating.
 * @param {Function} props.onThemeSavedSuccessfully - Callback invoked after successful save.
 */
export const FarmeandoAuraThemeModal = ({
    isOpen,
    onCloseModal,
    initialThemeData = null,
    onThemeSavedSuccessfully
}) => {
    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('general');
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');
    const [isBatchUploading, setIsBatchUploading] = useState(false);

    // Initial state template
    const defaultThemeStructure = {
        id: 0,
        name: '',
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        coverTitle: 'FARMEANDO AURA',

        // Portada
        coverBackgroundType: 'color',
        coverBackgroundColor: '#0a0a14',
        coverBackgroundUrl: '',
        coverTextColor: '#ffffff',

        // Escenario de Combate
        gameBackgroundType: 'color',
        gameBackgroundColor: '#121224',
        gameBackgroundUrl: '',
        gameTextColor: '#ffffff',

        // Vista Móvil Aficionado
        fanBackgroundType: 'color',
        fanBackgroundColor: '#0d0e1a',
        fanBackgroundUrl: '',
        fanTextColor: '#ffffff',

        // Avatares
        player1Avatar: '',
        player1Winner: '',
        player2Avatar: '',
        player2Winner: '',

        // Estilos
        player1Color: '#00ffcc',
        player2Color: '#ff0055',
        fontFamily: 'Bangers',

        // Baraja de transformaciones
        deckImages: [],

        // Frases de combos
        comboTexts: [...DEFAULT_COMBO_LEVELS]
    };

    const [themeFormState, setThemeFormState] = useState(defaultThemeStructure);

    useEffect(() => {
        if (initialThemeData && initialThemeData.id > 0) {
            setThemeFormState({
                ...defaultThemeStructure,
                ...initialThemeData,
                deckImages: Array.isArray(initialThemeData.deckImages)
                    ? [...initialThemeData.deckImages]
                    : [],
                comboTexts: Array.isArray(initialThemeData.comboTexts) && initialThemeData.comboTexts.length > 0
                    ? [...initialThemeData.comboTexts]
                    : [...DEFAULT_COMBO_LEVELS]
            });
        } else {
            setThemeFormState(defaultThemeStructure);
        }
        setActiveTabKey('general');
    }, [initialThemeData, isOpen]);

    if (!isOpen) return null;

    /**
     * Resolves absolute media URLs for rendering previews.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
    };

    /**
     * Updates an individual scalar property on the theme form state.
     */
    const handleFieldChange = (fieldName, fieldValue) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    /**
     * Handles single media upload.
     */
    const handleSingleFileUpload = async (fileObject, mediaCategory, targetFieldName) => {
        setActiveUploadFieldKey(targetFieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y optimizando archivo...' });

        try {
            const uploadResponse = await UploadMedia(fileObject, mediaCategory);
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo cargado con éxito.' });
                handleFieldChange(targetFieldName, uploadResponse.url);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'Error al subir el archivo multimedia.'
                });
            }
        } catch (uploadException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado en la subida de archivo.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    /**
     * Handles batch deck upload.
     */
    const handleBatchDeckUpload = async (filesArray) => {
        if (filesArray.length === 0) return;
        setIsBatchUploading(true);
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: `Subiendo ${filesArray.length} imágenes para la baraja...`
        });

        try {
            const batchResponse = await UploadBatchMedia(filesArray, 'deck');
            if (batchResponse.success && Array.isArray(batchResponse.urls)) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Se agregaron ${batchResponse.urls.length} imágenes a la baraja de transformaciones.`
                });
                setThemeFormState((previousState) => {
                    const combinedDeck = [...previousState.deckImages, ...batchResponse.urls].slice(0, 50);
                    return {
                        ...previousState,
                        deckImages: combinedDeck
                    };
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: batchResponse.message || 'Error al procesar el lote de imágenes.'
                });
            }
        } catch (batchException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado durante la subida en lote.' });
        } finally {
            setIsBatchUploading(false);
        }
    };

    /**
     * Updates an individual slot in the transformation deck.
     */
    const handleSingleDeckSlotUpload = async (fileObject, slotIndex) => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Actualizando slot #${slotIndex + 1}...` });
        try {
            const uploadResponse = await UploadMedia(fileObject, 'deck');
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Slot #${slotIndex + 1} actualizado.` });
                setThemeFormState((previousState) => {
                    const updatedDeck = [...previousState.deckImages];
                    updatedDeck[slotIndex] = uploadResponse.url;
                    return {
                        ...previousState,
                        deckImages: updatedDeck
                    };
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: uploadResponse.message || 'Error al actualizar slot.' });
            }
        } catch (slotException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error en la subida del slot.' });
        }
    };

    /**
     * Removes an image from the transformation deck.
     */
    const handleRemoveDeckSlot = (slotIndexToRemove) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            deckImages: previousState.deckImages.filter((_, index) => index !== slotIndexToRemove)
        }));
    };

    /**
     * Adds an empty or default transformation slot.
     */
    const handleAddNewDeckSlot = () => {
        if (themeFormState.deckImages.length >= 50) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La baraja soporta un máximo de 50 transformaciones.' });
            return;
        }
        setThemeFormState((previousState) => ({
            ...previousState,
            deckImages: [...previousState.deckImages, '']
        }));
    };

    /**
     * Clears all transformation deck slots.
     */
    const handleClearDeck = () => {
        setThemeFormState((previousState) => ({
            ...previousState,
            deckImages: []
        }));
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Baraja vaciada.' });
    };

    /**
     * Updates an attribute of a combo item.
     */
    const handleComboItemChange = (comboIndex, propertyName, propertyValue) => {
        setThemeFormState((previousState) => {
            const updatedCombos = [...previousState.comboTexts];
            updatedCombos[comboIndex] = {
                ...updatedCombos[comboIndex],
                [propertyName]: propertyValue
            };
            return {
                ...previousState,
                comboTexts: updatedCombos
            };
        });
    };

    /**
     * Restores default combo texts and thresholds.
     */
    const handleRestoreDefaultCombos = () => {
        setThemeFormState((previousState) => ({
            ...previousState,
            comboTexts: [...DEFAULT_COMBO_LEVELS]
        }));
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Frases de combos restablecidas a los valores por defecto.' });
    };

    /**
     * Submits the theme form to be saved on the backend.
     */
    const handleFormSubmit = async (formEvent) => {
        formEvent.preventDefault();

        if (!themeFormState.name || themeFormState.name.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Por favor asigna un nombre al tema.' });
            setActiveTabKey('general');
            return;
        }

        if (themeFormState.deckImages.length === 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La baraja debe contener al menos 1 transformación.' });
            setActiveTabKey('deck');
            return;
        }

        setIsSavingInProgress(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración del tema...' });

        try {
            const saveResponse = await SaveTheme(themeFormState);
            if (saveResponse.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Tema "${themeFormState.name}" guardado exitosamente.`
                });
                if (onThemeSavedSuccessfully) {
                    onThemeSavedSuccessfully(saveResponse.data);
                }
                onCloseModal();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResponse.message || 'Error al guardar el tema.'
                });
            }
        } catch (saveException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el tema.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    return (
        <>
            <div className="cModal__Background" onClick={onCloseModal}></div>
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '1000px', width: '95vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette-fill text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {themeFormState.id > 0 ? `Editar Tema: ${themeFormState.name}` : 'Crear Nuevo Tema - Farmeando Aura'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={onCloseModal}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Navigation Tabs Header */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'general' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('general')}
                                >
                                    <i className="bi bi-sliders me-1"></i> 1. General y Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'game' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('game')}
                                >
                                    <i className="bi bi-controller me-1"></i> 2. Escenario y Luchadores
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'fan' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('fan')}
                                >
                                    <i className="bi bi-phone me-1"></i> 3. Vista Móvil (Aficionado)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'deck' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('deck')}
                                >
                                    <i className="bi bi-collection-play me-1"></i> 4. Baraja ({themeFormState.deckImages.length}/50)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'combos' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('combos')}
                                >
                                    <i className="bi bi-lightning-charge me-1"></i> 5. Frases de Combos
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Scrollable Tab Body */}
                    <form onSubmit={handleFormSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 170px)' }}>
                            {/* ============================================================ */}
                            {/* TAB 1: GENERAL Y PORTADA                                     */}
                            {/* ============================================================ */}
                            {activeTabKey === 'general' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Nombre del Tema *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ej. Farmeando Aura ADSL Clásico, Modo Lucha Libre..."
                                            value={themeFormState.name}
                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DragAndDropField
                                            fieldLabel="Logo de Marca / Club (Brand)"
                                            targetFieldName="brandLogoUrl"
                                            mediaCategory="logo"
                                            currentValue={themeFormState.brandLogoUrl}
                                            onUploadFile={handleSingleFileUpload}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isProcessing={activeUploadFieldKey === 'brandLogoUrl'}
                                            helpText="Logo del ADSL o equipo local (PNG transparente)"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DragAndDropField
                                            fieldLabel="Logo del Patrocinador Oficial"
                                            targetFieldName="sponsorLogoUrl"
                                            mediaCategory="logo"
                                            currentValue={themeFormState.sponsorLogoUrl}
                                            onUploadFile={handleSingleFileUpload}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isProcessing={activeUploadFieldKey === 'sponsorLogoUrl'}
                                            helpText="Logo comercial o sponsor (PNG transparente)"
                                        />
                                    </div>

                                    <div className="col-12">
                                        <hr className="my-2" />
                                        <h6 className="fw-bold text-primary mb-3">
                                            <i className="bi bi-aspect-ratio me-1"></i> Personalización de la Portada Promocional
                                        </h6>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Título de Portada</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="FARMEANDO AURA"
                                            value={themeFormState.coverTitle || ''}
                                            onChange={(e) => handleFieldChange('coverTitle', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Fuente de Portada (Contraste)</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipo de Fondo de Portada</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.coverBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('coverBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen / Textura</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Fondo de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverBackgroundColor || '#0a0a14'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.coverBackgroundColor || '#0a0a14'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.coverBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo para Portada (1920x1080)"
                                                targetFieldName="coverBackgroundUrl"
                                                mediaCategory="background"
                                                currentValue={themeFormState.coverBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'coverBackgroundUrl'}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 2: ESCENARIO Y LUCHADORES                                */}
                            {/* ============================================================ */}
                            {activeTabKey === 'game' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <h6 className="fw-bold text-primary mb-2">
                                            <i className="bi bi-tv me-1"></i> Fondo del Escenario de Combate
                                        </h6>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo de Juego</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.gameBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('gameBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen de Estadio / Ring</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo de Juego</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameBackgroundColor || '#121224'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.gameBackgroundColor || '#121224'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto en Pantalla</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.gameBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo de Combate (1920x1080)"
                                                targetFieldName="gameBackgroundUrl"
                                                mediaCategory="background"
                                                currentValue={themeFormState.gameBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'gameBackgroundUrl'}
                                            />
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <hr className="my-2" />
                                        <h6 className="fw-bold text-primary mb-3">
                                            <i className="bi bi-people-fill me-1"></i> Avatares y Colores de Luchadores
                                        </h6>
                                    </div>

                                    {/* JUGADOR 1 */}
                                    <div className="col-md-6 border-end pe-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-info mb-3">
                                                <i className="bi bi-shield-shaded me-1"></i> Jugador 1 (Lado Izquierdo)
                                            </h6>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Color Primario J1</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={themeFormState.player1Color || '#00ffcc'}
                                                        onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control text-uppercase"
                                                        value={themeFormState.player1Color || '#00ffcc'}
                                                        onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <DragAndDropField
                                                fieldLabel="Avatar Inicial J1 (J1I.png)"
                                                targetFieldName="player1Avatar"
                                                mediaCategory="avatar"
                                                currentValue={themeFormState.player1Avatar}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player1Avatar'}
                                                helpText="Pose inicial de combate (500x500 PNG)"
                                            />

                                            <DragAndDropField
                                                fieldLabel="Pose de Victoria J1 (J1F.png)"
                                                targetFieldName="player1Winner"
                                                mediaCategory="avatar"
                                                currentValue={themeFormState.player1Winner}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player1Winner'}
                                                helpText="Pose de K.O. vencedor (500x500 PNG)"
                                            />
                                        </div>
                                    </div>

                                    {/* JUGADOR 2 */}
                                    <div className="col-md-6 ps-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-danger mb-3">
                                                <i className="bi bi-shield-shaded me-1"></i> Jugador 2 (Lado Derecho)
                                            </h6>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Color Primario J2</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={themeFormState.player2Color || '#ff0055'}
                                                        onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control text-uppercase"
                                                        value={themeFormState.player2Color || '#ff0055'}
                                                        onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <DragAndDropField
                                                fieldLabel="Avatar Inicial J2 (J2I.png)"
                                                targetFieldName="player2Avatar"
                                                mediaCategory="avatar"
                                                currentValue={themeFormState.player2Avatar}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player2Avatar'}
                                                helpText="Pose inicial de combate (500x500 PNG)"
                                            />

                                            <DragAndDropField
                                                fieldLabel="Pose de Victoria J2 (J2F.png)"
                                                targetFieldName="player2Winner"
                                                mediaCategory="avatar"
                                                currentValue={themeFormState.player2Winner}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player2Winner'}
                                                helpText="Pose de K.O. vencedor (500x500 PNG)"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipografía Principal</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Bangers"
                                            value={themeFormState.fontFamily || 'Bangers'}
                                            onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 3: VISTA MÓVIL (AFICIONADO)                              */}
                            {/* ============================================================ */}
                            {activeTabKey === 'fan' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <h6 className="fw-bold text-primary mb-2">
                                            <i className="bi bi-phone me-1"></i> Personalización de la Pantalla Móvil del Aficionado (/farmeando-aura)
                                        </h6>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo Móvil</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.fanBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('fanBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen Vertical / Patrón</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanBackgroundColor || '#0d0e1a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.fanBackgroundColor || '#0d0e1a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.fanBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo Móvil (1080x1920 Vertical)"
                                                targetFieldName="fanBackgroundUrl"
                                                mediaCategory="background"
                                                currentValue={themeFormState.fanBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'fanBackgroundUrl'}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 4: BARAJA DE TRANSFORMACIONES (1..50)                   */}
                            {/* ============================================================ */}
                            {activeTabKey === 'deck' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h6 className="fw-bold text-primary m-0">
                                                <i className="bi bi-grid-3x3-gap-fill me-1"></i> Baraja de Transformaciones de Aura ({themeFormState.deckImages.length} slots activos)
                                            </h6>
                                            <small className="text-muted">
                                                Los personajes cambian automáticamente cada 15 taps promedio acumulados durante el combate sin repetición.
                                            </small>
                                        </div>
                                        <div className="d-flex gap-2">
                                            {themeFormState.deckImages.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger fw-bold"
                                                    onClick={handleClearDeck}
                                                >
                                                    <i className="bi bi-trash me-1"></i> Vaciar Baraja
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary fw-bold"
                                                onClick={handleAddNewDeckSlot}
                                                disabled={themeFormState.deckImages.length >= 50}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i> Agregar Slot ({themeFormState.deckImages.length}/50)
                                            </button>
                                        </div>
                                    </div>

                                    {/* Zona de Drop Masivo */}
                                    <BatchDeckDropzone
                                        onUploadBatch={handleBatchDeckUpload}
                                        isUploading={isBatchUploading}
                                    />

                                    {/* Grilla visual de slots */}
                                    <div className="row g-3">
                                        {themeFormState.deckImages.map((deckImageUrl, slotIndex) => (
                                            <div key={slotIndex} className="col-6 col-sm-4 col-md-3 col-lg-2">
                                                <div className="card h-100 border shadow-sm rounded-3 overflow-hidden text-center position-relative">
                                                    <span className="badge bg-dark position-absolute top-0 start-0 m-1">
                                                        #{slotIndex + 1}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0 px-1 rounded-circle"
                                                        onClick={() => handleRemoveDeckSlot(slotIndex)}
                                                        title="Eliminar este slot"
                                                    >
                                                        <i className="bi bi-x"></i>
                                                    </button>
                                                    <div
                                                        className="p-2 bg-light d-flex align-items-center justify-content-center"
                                                        style={{ height: '110px' }}
                                                    >
                                                        <img
                                                            src={resolveMediaUrl(deckImageUrl)}
                                                            alt={`Slot ${slotIndex + 1}`}
                                                            style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }}
                                                            onError={(e) => {
                                                                e.target.src = '/img/games/slotmachine/logotipo-five-digital-minds-azul.png';
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="p-2 border-top bg-white">
                                                        <label
                                                            htmlFor={`deck-slot-input-${slotIndex}`}
                                                            className="btn btn-xs btn-outline-primary w-100 py-1 fw-bold text-truncate"
                                                            style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                                                        >
                                                            <i className="bi bi-arrow-repeat me-1"></i> Reemplazar
                                                        </label>
                                                        <input
                                                            id={`deck-slot-input-${slotIndex}`}
                                                            type="file"
                                                            accept="image/*"
                                                            className="d-none"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) handleSingleDeckSlotUpload(file, slotIndex);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 5: FRASES DE COMBOS                                      */}
                            {/* ============================================================ */}
                            {activeTabKey === 'combos' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h6 className="fw-bold text-primary m-0">
                                                <i className="bi bi-stars me-1"></i> Frases y Efectos de Combos de Aura
                                            </h6>
                                            <small className="text-muted">
                                                Personaliza los mensajes en pantalla para marcas, patrocinadores o activaciones especiales.
                                            </small>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary fw-bold"
                                            onClick={handleRestoreDefaultCombos}
                                        >
                                            <i className="bi bi-arrow-counterclockwise me-1"></i> Restaurar Frases por Defecto
                                        </button>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover align-middle">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th style={{ width: '80px' }}>Nivel</th>
                                                    <th style={{ width: '110px' }}>Umbral Taps</th>
                                                    <th>Texto del Combo</th>
                                                    <th style={{ width: '120px' }}>Puntos Extra</th>
                                                    <th style={{ width: '110px' }}>Tamaño</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {themeFormState.comboTexts.map((comboItem, comboIndex) => (
                                                    <tr key={comboIndex}>
                                                        <td className="text-center fw-bold">
                                                            <span className="badge bg-primary fs-6">Nivel {comboItem.level}</span>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center fw-bold"
                                                                value={comboItem.threshold}
                                                                onChange={(e) =>
                                                                    handleComboItemChange(comboIndex, 'threshold', Number(e.target.value))
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm fw-bold text-primary"
                                                                value={comboItem.text}
                                                                onChange={(e) =>
                                                                    handleComboItemChange(comboIndex, 'text', e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center"
                                                                value={comboItem.extraPoints}
                                                                onChange={(e) =>
                                                                    handleComboItemChange(comboIndex, 'extraPoints', Number(e.target.value))
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm text-center"
                                                                value={comboItem.fontSize || '5rem'}
                                                                onChange={(e) =>
                                                                    handleComboItemChange(comboIndex, 'fontSize', e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3 bg-light border-top d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={onCloseModal}
                                disabled={isSavingInProgress}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-success fw-bold px-4"
                                disabled={isSavingInProgress}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-1"></i> Guardar Tema
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};
